import useMediaQuery from '@mui/material/useMediaQuery';
import Button, { OUTLINED_BUTTON_TYPE_DARK } from 'components/Button/Button';
import { ArrowLeft, ArrowRight } from 'components/icons';
import { Image } from 'components/ImageComponent';
import Link from 'next/link';
import { type FC, useCallback, useEffect, useState } from 'react';
import { OurHotelsInType } from 'types/types';
import styles from './OurHotelsIn.module.css';
import useEmblaCarousel, { type EmblaOptionsType } from 'embla-carousel-react';

interface IOurHotelsIn {
  location: string;
  hotels: OurHotelsInType[];
  maxPerPage: number;
}

interface ISelectors {
  current: number;
  max: number;
}

const OurHotelsIn: FC<IOurHotelsIn> = ({ location, hotels, maxPerPage }) => {
  const isMobile = useMediaQuery(`(max-width:569px)`);
  const [current, setCurrent] = useState(0);
  const [hotelsByPage, setHotelsByPage] = useState([] as OurHotelsInType[][]);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
  } as EmblaOptionsType);

  const calcPerPage = () => {
    let tempByPage = [] as OurHotelsInType[][];
    const moduleCalc = Math.floor(window.innerWidth / 285);
    if (moduleCalc <= 1) {
      for (let i = 0; i < hotels.length; i++) {
        tempByPage.push([hotels[i]]);
      }
    } else {
      const perPage = moduleCalc < maxPerPage ? moduleCalc : maxPerPage;
      for (let i = 0; i < hotels.length; i += perPage) {
        tempByPage.push(hotels.slice(i, i + perPage));
      }
    }
    setHotelsByPage(tempByPage);
    setCurrent(0);
    emblaApi?.reInit();
    moduleCalc <= 1 && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
      ? null
      : emblaApi?.scrollTo(0, true);
  };

  useEffect(() => {
    calcPerPage();
    window.addEventListener('resize', () => {
      calcPerPage();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotels, maxPerPage, emblaRef, emblaApi]);

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi]
  );

  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi]
  );

  const handleCurrentChange = useCallback(
    (newCurrent: number) => {
      if (newCurrent < 0) {
        setCurrent(hotelsByPage.length - 1);
      } else if (newCurrent >= hotelsByPage.length) {
        setCurrent(0);
      } else {
        setCurrent(newCurrent);
      }
    },
    [hotelsByPage.length]
  );

  const Selectors: FC<ISelectors> = ({ current, max }) => {
    return (
      <div className={styles.selector}>
        <p className="demi-bold">
          {max >= 10 && current + 1 < 10 ? '0' + (current + 1) : current + 1} /{' '}
          {max}
        </p>
        <Button
          title={<ArrowLeft />}
          onClick={() =>
            current === 0
              ? null
              : (handleCurrentChange(current - 1), scrollPrev())
          }
          buttonType={OUTLINED_BUTTON_TYPE_DARK}
          className={`${styles.button} ${
            current === 0 ? styles.buttonDisabled : ''
          }`}
        />
        <Button
          title={<ArrowRight />}
          onClick={() =>
            current === hotelsByPage.length - 1
              ? null
              : (handleCurrentChange(current + 1), scrollNext())
          }
          buttonType={OUTLINED_BUTTON_TYPE_DARK}
          className={`${styles.button} ${
            current === hotelsByPage.length - 1 ? styles.buttonDisabled : ''
          }`}
        />
      </div>
    );
  };

  return (
    <div>
      {hotelsByPage.length > 0 && (
        <div className={`container ${styles.container}`}>
          <h3>Other hotels in {location}</h3>
          {!isMobile && (
            <Selectors current={current} max={hotelsByPage.length} />
          )}
        </div>
      )}
      <div
        className={`${isMobile ? 'outOfBounds' : 'container'} ${styles.embla}`}
      >
        <div className={styles.viewport} ref={emblaRef}>
          <div className={styles.carrouselContainer}>
            {hotelsByPage.map((group, index) => (
              <div className={styles.slide} key={index}>
                {group.map(hotel => (
                  <div
                    className={styles.card}
                    key={hotel.id}
                  >
                    <Link href={`/our-hotels/${hotel.id}`}>
                      <a>
                        <Image
                          src={hotel.main_image}
                          alt={hotel.name}
                          style={{
                            aspectRatio: ' 51/29',
                            height: '100vh',
                            maxWidth: '255px',
                            maxHeight: '154px',
                          }}
                        />
                      </a>
                    </Link>
                    <h5>{hotel.name}</h5>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OurHotelsIn;
